import React from "react";
import { Chip } from "@mui/material";
import {
  Match,
  MatchMode,
  MatchRequestStatus,
  MatchStatus,
} from "../../services/MatchmakingService";
import { formatTableDate, parseTableDate } from "../../utils/tableDateFormat";

type Tone = { bg: string; fg: string };

const TONES: Record<string, Tone> = {
  green: { bg: "#e7f6ec", fg: "#1e7b3c" },
  blue: { bg: "#e6eef8", fg: "#2a5a8c" },
  orange: { bg: "#fdf1dc", fg: "#9a5b00" },
  red: { bg: "#fde8e8", fg: "#b42318" },
  grey: { bg: "#eeeeee", fg: "#555555" },
  purple: { bg: "#efe9f8", fg: "#5b3a8c" },
};

const STATUS_TONE: Record<MatchStatus, keyof typeof TONES> = {
  OPEN: "green",
  FULL: "blue",
  CLOSED: "grey",
  CANCELLED: "red",
};

const REQUEST_TONE: Record<MatchRequestStatus, keyof typeof TONES> = {
  PENDING: "orange",
  ACCEPTED: "green",
  DECLINED: "red",
  CANCELLED: "grey",
  REMOVED: "red",
};

export const titleCase = (value: string) =>
  value.charAt(0) + value.slice(1).toLowerCase();

export const Badge: React.FC<{ label: string; tone: keyof typeof TONES }> = ({
  label,
  tone,
}) => (
  <Chip
    size="small"
    label={label}
    sx={{
      height: 22,
      fontSize: "0.72rem",
      fontWeight: 600,
      bgcolor: TONES[tone].bg,
      color: TONES[tone].fg,
    }}
  />
);

export const StatusBadge: React.FC<{ status: MatchStatus }> = ({ status }) => (
  <Badge label={titleCase(status)} tone={STATUS_TONE[status] || "grey"} />
);

export const ModeBadge: React.FC<{ mode: MatchMode }> = ({ mode }) => (
  <Badge label={titleCase(mode)} tone={mode === "PRIVATE" ? "purple" : "blue"} />
);

export const RequestStatusBadge: React.FC<{ status: MatchRequestStatus }> = ({
  status,
}) => <Badge label={titleCase(status)} tone={REQUEST_TONE[status] || "grey"} />;

/** Cancelled wins over paid: a refunded booking can still carry the paid flag. */
export const bookingState = (match: Pick<Match, "bookingPaid" | "bookingCancelled">) =>
  match.bookingCancelled ? "Cancelled" : match.bookingPaid ? "Paid" : "Unpaid";

export const BookingStateBadge: React.FC<{ match: Match }> = ({ match }) => {
  const state = bookingState(match);
  return (
    <Badge
      label={state}
      tone={state === "Cancelled" ? "red" : state === "Paid" ? "green" : "orange"}
    />
  );
};

/** Same 12-hour style as formatTableDateTime. */
export const formatTime = (value: any): string => {
  const d = parseTableDate(value);
  if (!d) return "-";
  const hours12 = d.getHours() % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${hours12}:${minutes} ${d.getHours() >= 12 ? "PM" : "AM"}`;
};

export const formatSlot = (startAt: any, endAt: any) =>
  `${formatTableDate(startAt)} · ${formatTime(startAt)} – ${formatTime(endAt)}`;

export const courtLabel = (match: Match) => {
  const unit = match.appointment?.unit;
  const venue = unit?.business?.name || "-";
  const court = unit?.short_name || unit?.name || "-";
  return `${venue} · ${court}`;
};
